import React, { FC, Fragment, memo, useEffect, useRef } from 'react';
import { useApolloClient } from 'react-apollo';
import { useTranslation } from 'react-i18next';
import { Button, Fade, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Event as StreamChatEvent, Message } from 'stream-chat';
import { Channel as StreamChannel, MessageList, MessageInput, Window } from 'stream-chat-react';
import { some, noop } from 'lodash';

import { useGetContentLazyQuery } from 'api';
import { Events } from 'analytics';
import { useCurrentUser, useNotify } from 'hooks';
import { ContentProvider } from 'providers';
import { Loaders } from 'components';
import { content as contentReducer } from 'apollo/reducers';
import { Index } from 'Links';

import ChannelHeader from './ChannelHeader';
import EmptyStateList from './EmptyStateList';
import configureChannel from './helpers';
import MessageInputCustom from './MessageInputCustom';
import useChatContext from './useChatContext';

interface IProps {
  onToggleDrawer: () => void;
}

const useStyles = makeStyles(theme => ({
  emptyChannel: {
    height: '100%',
    padding: theme.spacing(5),
    backgroundColor: theme.palette.common.white,
    borderTopLeftRadius: theme.spacing(),
    borderTopRightRadius: theme.spacing(),
  },
  channelCTA: {
    marginTop: theme.spacing(3),
    padding: 0,
    fontSize: theme.typography.pxToRem(24),
    fontWeight: theme.typography.fontWeightBold,
  },
}));

const Channel: FC<IProps> = ({ onToggleDrawer }) => {
  const classes = useStyles();
  const commentedRef = useRef<boolean>(false);
  const apolloClient = useApolloClient();
  const currentUser = useCurrentUser();
  const notify = useNotify();
  const { channel, client } = useChatContext();
  const { t } = useTranslation();

  const [getContentQuery, { data, loading, error }] = useGetContentLazyQuery({
    onError: notify.mutationError,
  });

  useEffect(() => {
    if (data?.getContent) {
      const channelData = configureChannel(data.getContent);
      const { name, image } = channel.data;
      if (channelData.name !== name || channelData.image !== image) {
        channel.update(channelData);
      }
    }
  }, [data?.getContent]);

  useEffect(() => {
    if (channel?.id) {
      getContentQuery({
        variables: {
          content_id: channel.id,
        },
      });
    }
  }, [channel]);

  useEffect(() => {
    function trackViewed() {
      if (!channel?.state) {
        return;
      }

      const { membership } = channel.state;
      if (!membership?.role && !commentedRef.current) {
        analytics.track(Events.DiscussionViewed);
      }
    }
    return () => trackViewed();
  }, [channel]);

  useEffect(() => {
    function trackComment(event: StreamChatEvent) {
      const { user, message } = event;

      contentReducer({ name: 'newComment', cache: apolloClient, id: channel.id });

      if (user.id !== currentUser.id) {
        return;
      }

      const mentionedIds = message.mentioned_users.map(mentioned => mentioned.id);
      if (mentionedIds.length) {
        channel.addMembers(mentionedIds);
      }

      analytics.track(Events.CommentUnique, {
        text_comment: !!message.text,
        image_comment: some(message.attachments, ['type', 'image']),
        url_comment: some(message.attachments, 'og_scrape_url'),
        file_comment: false,
        mention_comment: !!message.mentioned_users.length,
        reply_comment: false,
      });
    }

    client.on('message.new', trackComment);
    return () => client.off('message.new', trackComment);
  }, [client, channel]);

  function handleAddMember(message: Message) {
    commentedRef.current = true;
    channel.sendMessage(message);
    channel.addMembers([currentUser.id]);
  }

  function renderLoader() {
    return <Loaders.Standard />;
  }

  function renderEmptyChannel() {
    return (
      <div className="str-chat str-chat-channel messaging light">
        <div className="str-chat__container">
          <div className="str-chat__main-panel">
            <div className={classes.emptyChannel}>
              <Fade in timeout={3000}>
                <div>
                  <Typography variant="h4" paragraph>
                    {t('discussions:noChannelsTitle')}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('discussions:emptySubhead')}
                  </Typography>
                  <EmptyStateList />
                  <Button className={classes.channelCTA} color="primary" component={Index}>
                    {t('discussions:noChannelsCTA')}
                  </Button>
                </div>
              </Fade>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderEmptyMessageList() {
    return (
      <Fragment>
        <Typography variant="h4" paragraph>
          {t('discussions:noMessagesTitle')}
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          {t('discussions:emptySubhead')}
        </Typography>
        <EmptyStateList />
      </Fragment>
    );
  }

  if (loading) {
    return renderLoader();
  }

  if (error) {
    return null;
  }

  if (!data?.getContent) {
    return renderEmptyChannel();
  }

  return (
    <ContentProvider value={{ content: data?.getContent }}>
      <StreamChannel client={client} channel={channel} LoadingIndicator={renderLoader}>
        <Window>
          <ChannelHeader onToggleDrawer={onToggleDrawer} />
          <MessageList
            client={client}
            closeThread={noop}
            EmptyStateIndicator={renderEmptyMessageList}
          />
          <MessageInput focus Input={MessageInputCustom} overrideSubmitHandler={handleAddMember} />
        </Window>
      </StreamChannel>
    </ContentProvider>
  );
};

export default memo(Channel);
